import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: '',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: [],
  pos: [],
  cashier: [],
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpCashierPerformanceHour() {
  const mod = 'rp_cashier_performance_hour';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboPos, setCboPos] = useState([]);
  const [cboCashier, setCboCashier] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'branch_name', label: 'สาขา', align: 'left', sort: false },
    { id: 'cashier_name', label: 'แคชเชียร์', align: 'left', sort: false },
    { id: 'date_record', label: 'วันที่ขาย', align: 'left', sort: false },
    { id: 'date_start', label: 'วันที่เริ่มต้น', align: 'left', sort: false },
    { id: 'date_stop', label: 'วันที่สิ้นสุด', align: 'left', sort: false },
    { id: 'pos_name', label: 'pos', align: 'left', sort: false },
    { id: 'status', label: 'สถานะ', align: 'left', sort: false },
    { id: 'hour', label: 'ช่วงเวลา', align: 'left', sort: false },
    { id: 'service_time', label: 'เวลาบริการ', align: 'right', sort: false },
    { id: 'total_bill', label: 'จำนวนบิล', align: 'right', sort: false },
    { id: 'total_qty', label: 'จำนวนรายการ', align: 'right', sort: false },
    { id: 'total_sale', label: 'ขายรวมภาษี', align: 'right', sort: false },
    { id: 'time_per_bill', label: 'เวลา/บิล', align: 'right', sort: false },
    { id: 'time_per_qty', label: 'เวลา/รายการ', align: 'right', sort: false },
    { id: 'qty_per_bill', label: 'รายการ/บิล', align: 'right', sort: false },
    { id: 'sale_per_bill', label: 'เงิน/บิล', align: 'right', sort: false },
    { id: 'sale_per_qty', label: 'เงิน/รายการ', align: 'right', sort: false },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      pos: data.pos,
      cashier: data.cashier,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    setExcelLoading(true);

    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);

    find.branch.forEach((branchId) => {
      params.append('branch[]', branchId);
    });

    find.pos?.forEach((posId) => {
      params.append('pos[]', posId);
    });

    find.cashier?.forEach((cashierId) => {
      params.append('cashier[]', cashierId);
    });

    params.append('page', find.page);
    params.append('per_page', find.perPage);

    const url = `${urlExcel}/download?${params.toString()}`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;

    iframe.onload = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    iframe.onerror = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    document.body.appendChild(iframe);

    setTimeout(() => {
      setExcelLoading(false);
    }, 10000);
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const setDateRange = (start, stop) => {
    if (find.branch.length > 0) {
      view({
        ...find,
        date_start: Func.dateTimeFormat(start.toDate()),
        date_stop: Func.dateTimeFormat(stop.toDate()),
        page: 1,
      });
    }
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboPos(result.pos);
        setCboCashier(result.cashier);
        setUrlExcel(result.excel);

        const newFind = {
          ...find,
          branch: [],
          pos: result.pos.map((item) => item.id),
          cashier: result.cashier.map((item) => item.id),
          page: 1,
        };

        setFind(newFind);
        view(newFind);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboBranch.length) return 'ทั้งหมด';
                  return cboBranch
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.branch}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.branch.length === cboBranch.length;
                  view({
                    ...find,
                    branch: allSelected ? [] : cboBranch.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, branch: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.branch.length === cboBranch.length} />
                ทั้งหมด
              </MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.branch.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboCashier.length) return 'ทั้งหมด';
                  return cboCashier
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.cashier}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.cashier.length === cboCashier.length;
                  view({
                    ...find,
                    cashier: allSelected ? [] : cboCashier.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, cashier: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.cashier')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.cashier.length === cboCashier.length} />
                ทั้งหมด
              </MenuItem>
              {cboCashier.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.cashier.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboPos.length) return 'ทั้งหมด';
                  return cboPos
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.pos}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.pos.length === cboPos.length;
                  view({
                    ...find,
                    pos: allSelected ? [] : cboPos.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, pos: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('เครื่องขาย')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.pos.length === cboPos.length} />
                ทั้งหมด
              </MenuItem>
              {cboPos.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.pos.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={5}>
            <MDDateRange
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() => {
                if (find.branch.length > 0) {
                  view({ ...find, period: 'ALL', page: 1 });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              {find.branch.length === 0 ? null : (
                <MDButton
                  variant='gradient'
                  color='success'
                  onClick={excel}
                  disabled={excelLoading}
                >
                  {excelLoading ? (
                    <>
                      <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Icon>table_view</Icon>&nbsp;Excel
                    </>
                  )}
                </MDButton>
              )}
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_power_bi'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={initHeader.length} align='center'>
                        ไม่พบข้อมูลกรุณาเลือกสาขา
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='left'>{row.branch_name}</TableCell>
                      <TableCell align='left'>{row.cashier_name || '-'}</TableCell>
                      <TableCell align='left'>{row.date_record || '-'}</TableCell>
                      <TableCell align='left'>{row.date_start || '-'}</TableCell>
                      <TableCell align='left'>{row.date_stop || '-'}</TableCell>
                      <TableCell align='left'>{row.pos_name || '-'}</TableCell>
                      <TableCell align='left'>{row.status || '-'}</TableCell>
                      <TableCell align='left'>{row.hour}</TableCell>
                      <TableCell align='right'>{row.service_time}</TableCell>
                      <TableCell align='right'>{row.total_bill}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.total_qty)}</TableCell>
                      <TableCell align='right'>{row.total_sale}</TableCell>
                      <TableCell align='right'>{row.time_per_bill}</TableCell>
                      <TableCell align='right'>{row.time_per_qty}</TableCell>
                      <TableCell align='right'>{row.qty_per_bill}</TableCell>
                      <TableCell align='right'>{row.sale_per_bill}</TableCell>
                      <TableCell align='right'>{row.sale_per_qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            {record.length === 0 ? null : (
              <Card sx={{ p: 2 }}>
                <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                  <MDButton
                    variant={show ? 'contained' : 'outlined'}
                    color='dark'
                    onClick={openShow}
                  >
                    {t(`element.show_record`, { num: find.perPage })}&nbsp;
                    <Icon>keyboard_arrow_down</Icon>
                  </MDButton>
                  {renderShow}
                  <Pagination
                    count={pageTotal}
                    variant='outlined'
                    color='secondary'
                    showFirstButton
                    showLastButton
                    page={pageNo}
                    onChange={(e, p) => view({ ...find, page: p })}
                  />
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_power_bi`), t(`menu.rp_cashier_performance_hour`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpCashierPerformanceHour;
